import { RouteProp, useRoute } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import FullScreenSpinner from "components/FullScreenSpinner";
import MainContainer from "components/MainContainer";
import Stepper, { StepperContent } from "components/Stepper/Stepper";
import dayjs from "dayjs";
import { resetAddMembershipFlow } from "features/slices/addMembershipFlowSlice";
import {
  fetchAddMemberPlanPackages,
  fetchSignupCharge,
  resetBuyFlow,
  setSelectedPlanPackage,
} from "features/slices/buyFlowSlice";
import { PlanPackageUserFieldDetails, UserFields } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import { differenceBy } from "lodash";
import { ManageSubscriptionsStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import {
  addSubscriptionFetcher,
  updateAccountMemberRequest,
} from "queries/account.queries";
import { updateUserFetcher } from "queries/user.queries";
import React, { useEffect, useMemo } from "react";
import { View } from "react-native";
import StepThree from "screens/AddMembership/StepThree";
import { toManageMemberSubscription } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import toast from "utils/toast";
import StepOne from "./StepOne";

const AddNewPlan = () => {
  const { params } =
    useRoute<
      RouteProp<
        ManageSubscriptionsStackParamList,
        "AddNewPlanWithPlanPackageId"
      >
    >();

  const dispatch = useAppDispatch();
  const { account, updateFetchAccountBffCache } = useAccount();
  const {
    selectedPlanPackage,
    planPackages,
    selectedBillingInterval,
    step,
    signupCharge,
    planPackageUnavailable,
  } = useAppSelector((state) => state.buyFlow);

  const sendConversionData = useSendConversionData();

  const { mutateAsync: updateAccountMember, isLoading: updatingAccountMember } =
    useMutation(updateAccountMemberRequest);
  const { mutateAsync: updateUser } = useMutation(updateUserFetcher);
  const { mutateAsync: addSubscription, isLoading: loading } = useMutation(
    addSubscriptionFetcher
  );

  const currentMember = useMemo(() => {
    if (!account) return null;
    return account.members.find(
      (member) => member.id === Number(params?.memberId)
    );
  }, [account]);

  const currentMemberReservationPlans = useMemo(() => {
    if (!account || !currentMember) return [];

    return (
      account?.subscriptions
        .filter(
          (sub) =>
            sub.member &&
            sub.member.id === currentMember.id &&
            filterIneligibleSubscriptions(sub) &&
            !sub.child
        )
        .filter((sub) => getPlanType(sub.planPackage) === "RESERVATION")
        .map((sub) => sub.planPackage) || []
    );
  }, [account, currentMember]);

  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlanPackage?.id
  );
  const filteredPlanPackages = differenceBy(
    planPackages,
    currentMemberReservationPlans,
    "id"
  );

  const convertUserFieldsToCorrectFormat = (
    userFields: UserFields,
    planPackageUserFields: PlanPackageUserFieldDetails[]
  ) => {
    planPackageUserFields.forEach(({ field }) => {
      if (field.type === "DATE") {
        userFields[field.name] = dayjs(userFields[field.name]).format(
          "YYYY-MM-DD"
        );
      } else if (field.type === "DATETIME") {
        userFields[field.name] = dayjs(userFields[field.name]).format(
          "YYYY-MM-DD HH:mm:ss"
        );
      }
    });
    return userFields;
  };

  const addUserFieldsToUser = async (userFields: UserFields) => {
    if (userFields && Object.keys(userFields).length > 0) {
      await updateUser({
        userId: currentMember!.user.id,
        user: {
          ...currentMember!.user,
          ...userFields,
          userFields: {
            ...currentMember!.user.userFields,
            ...convertUserFieldsToCorrectFormat(
              userFields,
              planPackageUserFields!
            ),
          },
        },
      });
    }
  };

  const addSubscriptionToAccount = async (userFields?: UserFields) => {
    if (userFields) {
      await addUserFieldsToUser(userFields);
    }
    const updatedAccount = await addSubscription({
      billingInterval: selectedBillingInterval,
      userInfo: [
        {
          userId: currentMember!.user.id,
          createMemberPlanPackageParams: new Array(1).fill({
            planPackageId: selectedPlanPackage!.id,
          }),
        },
      ],
      accountId: account!.id,
      source: "FANRALLY_APP",
    });
    toManageMemberSubscription(String(currentMember!.id));
    updateFetchAccountBffCache(updatedAccount);
    dispatch(resetBuyFlow());
    dispatch(resetAddMembershipFlow());
    toast.success(i18n.t("screens.SubscriptionDetail.planAdded"));
    sendConversionData(signupCharge, 1);
  };

  // fetch available plan packages offered by tenant, if already initialized, skip
  useEffect(() => {
    if (planPackages.length > 0 || !account) return;

    dispatch(fetchAddMemberPlanPackages(account));
  }, []);

  // initialize selected plan package
  useEffect(() => {
    if (filteredPlanPackages.length === 0) return;
    if (params?.planPackageId) {
      const predefinedPlanPackage = filteredPlanPackages.find(
        (p) => p.id === Number(params.planPackageId)
      );
      if (predefinedPlanPackage) {
        dispatch(setSelectedPlanPackage(predefinedPlanPackage));
      } else {
        dispatch(setSelectedPlanPackage(filteredPlanPackages[0]));
      }
    }
  }, [planPackages]);

  useEffect(() => {
    if (!selectedPlanPackage) return;
    dispatch(
      fetchSignupCharge({
        billingInterval: selectedBillingInterval,
        planPackageIds: new Array(1).fill(selectedPlanPackage.id),
      })
    );
  }, [selectedPlanPackage, selectedBillingInterval]);

  if (!account) return null;

  if (planPackages.length === 0) {
    return (
      <Text category="h3" style={{ textAlign: "center" }}>
        Loading...
      </Text>
    );
  }

  if (updatingAccountMember || loading)
    return (
      <FullScreenSpinner
        text={i18n.t("screens.SubscriptionDetail.addingPlanToAccount")}
      ></FullScreenSpinner>
    );

  if (planPackageUnavailable) {
    return (
      <Text category="h3" style={{ textAlign: "center" }}>
        {i18n.t("screens.SubscriptionDetail.noAdditionalPlans")}
      </Text>
    );
  }

  return (
    <MainContainer>
      <View className="p-4">
        <Stepper
          step={step}
          steps={[{ name: "Choose Plan" }, { name: "Review" }]}
        >
          <StepperContent>
            <StepOne planPackages={filteredPlanPackages} />
          </StepperContent>
          {/* not showing StepTwo component becuase in the add plans flow, we don't want user to add a new member */}
          <StepperContent>
            {signupCharge && <StepThree onSubmit={addSubscriptionToAccount} />}
          </StepperContent>
        </Stepper>
      </View>
    </MainContainer>
  );
};

export default AddNewPlan;
